/**
 * Fetch robusto para rutas /api/admin/* y lecturas públicas del panel admin.
 * Hyperdrive paths: Railway directo → fallback www; cola serial; métricas PostHog/Datadog.
 */

import {
  trackAdminApiOutcome,
  transportFromRequestUrl,
  type AdminApiTransport,
} from "@/lib/adminObservability";
import {
  buildHyperdriveAdminUrls,
  buildSerializedPublicUrls,
  shouldSerializeAdminFetch,
} from "@/lib/hyperdriveAdminPaths";
import {
  dropQueuedLowPriorityAdminRequests,
  enqueueAdminRequest,
  inferAdminRequestPriority,
  shouldBypassAdminQueue,
  type AdminRequestPriority,
} from "@/lib/adminRequestQueue";

const MAX_ATTEMPTS_BY_PRIORITY: Record<AdminRequestPriority, number> = {
  critical: 6,
  normal: 4,
  low: 2,
};
const RETRY_BASE_MS = 700;
const FETCH_TIMEOUT_MS = 32_000;

function isRetryableNetworkError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /failed to fetch|networkerror|load failed|network request failed|connection reset|connection closed|err_connection|network changed|socket hang up|aborted|timeout/i.test(
    msg,
  );
}

function isRetryableHttpStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504 || status === 429;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function networkErrorMessage(path: string): Error {
  return new Error(
    `No se pudo conectar con ${path}. Reintentá en unos segundos o abrí /admin/config-health.`,
  );
}

function resolveFetchTargets(
  input: RequestInfo | URL,
  path: string,
  mode: "admin" | "public",
): Array<RequestInfo | URL> {
  if (typeof input !== "string" || !input.startsWith("/")) {
    return [input];
  }
  if (mode === "admin") {
    return buildHyperdriveAdminUrls(path);
  }
  return buildSerializedPublicUrls(path);
}

async function fetchOneWithRetry(
  input: RequestInfo | URL,
  init: RequestInit,
  path: string,
  method: string,
  maxAttempts: number,
): Promise<Response> {
  const crossOrigin =
    typeof input === "string" &&
    (input.startsWith("http://") || input.startsWith("https://"));
  const transport: AdminApiTransport = crossOrigin
    ? "railway"
    : transportFromRequestUrl(typeof input === "string" ? input : path);
  const started = Date.now();
  let lastErr: unknown;
  let attempts = 0;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    attempts = attempt;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(input, {
        ...init,
        credentials: crossOrigin ? "omit" : (init.credentials ?? "same-origin"),
        signal: controller.signal,
      });
      window.clearTimeout(timer);
      if (!res.ok && isRetryableHttpStatus(res.status) && attempt < maxAttempts) {
        await sleep(RETRY_BASE_MS * attempt);
        continue;
      }
      trackAdminApiOutcome(String(input), {
        path,
        method,
        status: res.status,
        durationMs: Date.now() - started,
        transport,
        attempts: attempt,
      });
      return res;
    } catch (err) {
      window.clearTimeout(timer);
      lastErr = err;
      if (!isRetryableNetworkError(err) || attempt === maxAttempts) break;
      await sleep(RETRY_BASE_MS * attempt);
    }
  }

  const errMsg = lastErr instanceof Error ? lastErr.message : String(lastErr);
  trackAdminApiOutcome(String(input), {
    path,
    method,
    status: "network_error",
    durationMs: Date.now() - started,
    transport,
    attempts,
    errorMessage: errMsg,
  });

  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}

async function fetchWithRetry(
  input: RequestInfo | URL,
  init: RequestInit,
  path: string,
  method: string,
  maxAttempts: number,
  mode: "admin" | "public",
): Promise<Response> {
  const targets = resolveFetchTargets(input, path, mode);
  let lastErr: unknown;

  for (let t = 0; t < targets.length; t += 1) {
    const target = targets[t];
    const hasFallback = t < targets.length - 1;
    try {
      const res = await fetchOneWithRetry(target, init, path, method, maxAttempts);
      // Railway puede ir adelantado/atrasado vs www — probar siguiente origen en 404.
      if (res.status === 404 && hasFallback) {
        await sleep(RETRY_BASE_MS);
        continue;
      }
      return res;
    } catch (err) {
      lastErr = err;
      if (!isRetryableNetworkError(err) || t === targets.length - 1) break;
      await sleep(RETRY_BASE_MS * 2);
    }
  }

  if (isRetryableNetworkError(lastErr)) {
    throw networkErrorMessage(path);
  }
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}

export type AdminApiFetchInit = RequestInit & {
  adminPriority?: AdminRequestPriority;
};

export async function adminApiFetch(
  getAuthHeaders: () => Promise<Record<string, string>>,
  input: RequestInfo | URL,
  init?: AdminApiFetchInit,
): Promise<Response> {
  const authHeaders = await getAuthHeaders();
  const bearer = authHeaders.Authorization?.trim() ?? "";
  if (!bearer.startsWith("Bearer ") || bearer.length < 20) {
    throw new Error("No autenticado — esperá a que Clerk cargue la sesión admin.");
  }
  const merged = new Headers(init?.headers);
  for (const [k, v] of Object.entries(authHeaders)) {
    merged.set(k, v);
  }

  const path =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.pathname
        : input.url;

  const method = init?.method ?? "GET";
  const priority =
    init?.adminPriority ?? inferAdminRequestPriority(path, method);

  const { adminPriority: _drop, ...fetchInit } = init ?? {};
  const maxAttempts = MAX_ATTEMPTS_BY_PRIORITY[priority];

  const run = () =>
    fetchWithRetry(
      input,
      {
        ...fetchInit,
        headers: merged,
        cache: fetchInit.cache ?? "no-store",
        credentials: fetchInit.credentials ?? "same-origin",
      },
      path,
      method,
      maxAttempts,
      "admin",
    );

  if (shouldBypassAdminQueue(path, method)) {
    dropQueuedLowPriorityAdminRequests();
    return run();
  }

  return enqueueAdminRequest(run, priority);
}

export async function adminPublicFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const path =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.pathname
        : input.url;

  const method = init?.method ?? "GET";
  const run = () =>
    fetchWithRetry(
      input,
      {
        ...init,
        cache: init?.cache ?? "no-store",
        credentials: init?.credentials ?? "same-origin",
      },
      path,
      method,
      MAX_ATTEMPTS_BY_PRIORITY.normal,
      "public",
    );

  if (shouldSerializeAdminFetch(path)) {
    return enqueueAdminRequest(run, "normal");
  }

  return run();
}